import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, TextField, Button, Alert, Divider,
  Card, CardContent, CardHeader, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, Grid, InputAdornment,
  Tabs, Tab, Container, Paper, alpha, useTheme, FormControlLabel, Checkbox, Switch
} from '@mui/material';
import {
  Settings, UploadFile, Save, Close, AttachMoney, QrCode, ReceiptLong, LocalOffer, CheckCircle, Notifications as NotifIcon, PauseCircle, Refresh
} from '@mui/icons-material';

import { gymUpiAPI, gymsAPI, uploadAPI, SERVER_URL } from '../services/api';
import { CURRENCY_SYMBOL, formatCurrency } from '../utils/currency';
import PageHeader from '../components/PageHeader';

function fmtPrice(price) {
  return formatCurrency(price);
}

export default function GymSettings() {
  const theme = useTheme();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const gymId = currentUser?.gymId?._id || currentUser?.gymId;

  // UPI Settings state
  const [upiSettings, setUpiSettings] = useState({
    upiId: '',
    qrImage: '',
    invoiceReminderDays: 3,
    invoiceNote: '',
  });
  const [upiEditForm, setUpiEditForm] = useState({ ...upiSettings });
  const [upiLoading, setUpiLoading] = useState(true);
  const [upiSaving, setUpiSaving] = useState(false);
  const [qrUploading, setQrUploading] = useState(false);
  const [qrPreviewOpen, setQrPreviewOpen] = useState(false);

  // Pricing state
  const [pricing, setPricing] = useState({
    monthly: { price: 0 },
    quarterly: { price: 0, discount: 0 },
    annual: { price: 0, discount: 0 },
  });
  const [pricingEditForm, setPricingEditForm] = useState({ ...pricing });
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingSaving, setPricingSaving] = useState(false);

  const [selectedTab, setSelectedTab] = useState('upi');
  const [alerts, setAlerts] = useState({ upi: null, pricing: null });

  // Notification Settings state
  const [notificationSettings, setNotificationSettings] = useState({
    memberEnrolled: true,
    memberLeft: true,
    feePaid: true,
    feeFailed: true,
    planDownloaded: false,
    staffAdded: true,
    memberRejoined: true,
    challengeEnrolled: true,
    newLead: true,
    leadConverted: true,
    feedbackReceived: true,
    attendanceMarked: false,
    slotBooked: false,
    lowAttendanceAlert: true,
    batchCreated: true,
    memberMilestone: true,
  });
  const [notificationEditForm, setNotificationEditForm] = useState({ ...notificationSettings });
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);

  // Pause Settings state
  const [pauseSettings, setPauseSettings] = useState({
    allowPauseMembership: false,
    maxPauseDays: 30,
  });
  const [pauseEditForm, setPauseEditForm] = useState({ ...pauseSettings });
  const [pauseLoading, setPauseLoading] = useState(true);
  const [pauseSaving, setPauseSaving] = useState(false);

  const loadUpiSettings = useCallback(async () => {
    if (!gymId) return;
    setUpiLoading(true);
    try {
      const res = await gymUpiAPI.getSettings(gymId);
      const data = res.data.data || {};
      setUpiSettings(data);
      setUpiEditForm({
        upiId: data.upiId || '',
        qrImage: data.qrImage || '',
        invoiceReminderDays: data.invoiceReminderDays ?? 3,
        invoiceNote: data.invoiceNote || '',
      });
    } catch (e) {
      setAlerts(prev => ({ ...prev, upi: { type: 'error', msg: e.response?.data?.message || 'Failed to load UPI settings' } }));
    } finally {
      setUpiLoading(false);
    }
  }, [gymId]);

  const loadPricing = useCallback(async () => {
    if (!gymId) return;
    setPricingLoading(true);
    try {
      const res = await gymsAPI.getOne(gymId);
      const data = res.data.data || {};
      if (data.pricing) {
        setPricing(data.pricing);
        setPricingEditForm(data.pricing);
      }
    } catch (e) {
      setAlerts(prev => ({ ...prev, pricing: { type: 'error', msg: e.response?.data?.message || 'Failed to load pricing' } }));
    } finally {
      setPricingLoading(false);
    }
  }, [gymId]);

  const loadNotificationSettings = useCallback(async () => {
    if (!gymId) return;
    setNotifLoading(true);
    try {
      const res = await gymsAPI.getNotificationSettings(gymId);
      const data = res.data.data || notificationSettings;
      setNotificationSettings(data);
      setNotificationEditForm(data);
    } catch (e) {
      console.error('Failed to load notification settings:', e);
      setNotifLoading(false);
    } finally {
      setNotifLoading(false);
    }
  }, [gymId, notificationSettings]);

  const loadPauseSettings = useCallback(async () => {
    if (!gymId) return;
    setPauseLoading(true);
    try {
      const res = await gymsAPI.getPauseSettings(gymId);
      const data = res.data.data || {};
      const settings = {
        allowPauseMembership: Boolean(data.allowPauseMembership),
        maxPauseDays: data.maxPauseDays ?? 30,
      };
      setPauseSettings(settings);
      setPauseEditForm(settings);
    } catch (e) {
      console.error('Failed to load pause settings:', e);
    } finally {
      setPauseLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    loadUpiSettings();
    loadPricing();
    loadNotificationSettings();
    loadPauseSettings();
  }, [loadUpiSettings, loadPricing, loadNotificationSettings, loadPauseSettings]);

  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAlerts(prev => ({ ...prev, upi: { type: 'error', msg: 'Only image files are allowed' } }));
      return;
    }

    setQrUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      const uploadedUrl = res.data.data.fileUrl || res.data.data.url;
      setUpiEditForm(prev => ({ ...prev, qrImage: uploadedUrl }));
      setAlerts(prev => ({ ...prev, upi: { type: 'success', msg: 'QR image uploaded successfully' } }));
    } catch (e) {
      setAlerts(prev => ({ ...prev, upi: { type: 'error', msg: e.response?.data?.message || 'Failed to upload QR image' } }));
    } finally {
      setQrUploading(false);
    }
  };

  const saveUpiSettings = async () => {
    if (!gymId) return setAlerts(prev => ({ ...prev, upi: { type: 'error', msg: 'No gym associated' } }));
    if (!upiEditForm.upiId?.trim()) return setAlerts(prev => ({ ...prev, upi: { type: 'error', msg: 'UPI ID is required' } }));

    setUpiSaving(true);
    setAlerts(prev => ({ ...prev, upi: null }));
    try {
      await gymUpiAPI.upsertSettings(gymId, upiEditForm);
      setUpiSettings(upiEditForm);
      setAlerts(prev => ({ ...prev, upi: { type: 'success', msg: 'UPI settings saved successfully' } }));
    } catch (e) {
      setAlerts(prev => ({ ...prev, upi: { type: 'error', msg: e.response?.data?.message || 'Failed to save UPI settings' } }));
    } finally {
      setUpiSaving(false);
    }
  };

  const savePricing = async () => {
    if (!gymId) return setAlerts(prev => ({ ...prev, pricing: { type: 'error', msg: 'No gym associated' } }));

    setPricingSaving(true);
    setAlerts(prev => ({ ...prev, pricing: null }));
    try {
      await gymsAPI.updatePricing(gymId, pricingEditForm);
      setPricing(pricingEditForm);
      setAlerts(prev => ({ ...prev, pricing: { type: 'success', msg: 'Membership pricing saved successfully' } }));
    } catch (e) {
      setAlerts(prev => ({ ...prev, pricing: { type: 'error', msg: e.response?.data?.message || 'Failed to save pricing' } }));
    } finally {
      setPricingSaving(false);
    }
  };

  const handleUpiFieldChange = (field, value) => setUpiEditForm(prev => ({ ...prev, [field]: value }));
  const handlePricingFieldChange = (plan, field, value) => setPricingEditForm(prev => ({ ...prev, [plan]: { ...prev[plan], [field]: value } }));
  const handleNotificationFieldChange = (field, value) => setNotificationEditForm(prev => ({ ...prev, [field]: value }));
  const handlePauseFieldChange = (field, value) => setPauseEditForm(prev => ({ ...prev, [field]: value }));
  const resetUpiForm = () => setUpiEditForm({ ...upiSettings });
  const resetPricingForm = () => setPricingEditForm({ ...pricing });
  const resetNotificationForm = () => setNotificationEditForm({ ...notificationSettings });
  const resetPauseForm = () => setPauseEditForm({ ...pauseSettings });
  const getQrImageUrl = (path) => path ? (path.startsWith('http') ? path : `${SERVER_URL}/${path}`) : null;

  const saveNotificationSettings = async () => {
    if (!gymId) return setAlerts(prev => ({ ...prev, notifications: { type: 'error', msg: 'No gym associated' } }));

    setNotifSaving(true);
    setAlerts(prev => ({ ...prev, notifications: null }));
    try {
      await gymsAPI.saveNotificationSettings(gymId, notificationEditForm);
      setNotificationSettings(notificationEditForm);
      setAlerts(prev => ({ ...prev, notifications: { type: 'success', msg: 'Notification settings saved successfully' } }));
    } catch (e) {
      setAlerts(prev => ({ ...prev, notifications: { type: 'error', msg: e.response?.data?.message || 'Failed to save notification settings' } }));
    } finally {
      setNotifSaving(false);
    }
  };

  const savePauseSettings = async () => {
    if (!gymId) return setAlerts(prev => ({ ...prev, pause: { type: 'error', msg: 'No gym associated' } }));

    setPauseSaving(true);
    setAlerts(prev => ({ ...prev, pause: null }));
    try {
      await gymsAPI.savePauseSettings(gymId, pauseEditForm);
      setPauseSettings(pauseEditForm);
      setAlerts(prev => ({ ...prev, pause: { type: 'success', msg: 'Membership pause settings saved successfully' } }));
    } catch (e) {
      setAlerts(prev => ({ ...prev, pause: { type: 'error', msg: e.response?.data?.message || 'Failed to save pause settings' } }));
    } finally {
      setPauseSaving(false);
    }
  };

  return (
    <Box>

      {/* Header */}
      <PageHeader
        title="Gym Settings"
        subtitle="Manage your payment gateway, invoices, membership pricing, and pause controls."
        gradient="linear-gradient(90deg, #7C3AED, #A78BFA)"
        actions={
          <Button variant="outlined" size="small" startIcon={<Refresh />}
            onClick={() => { loadUpiSettings(); loadPricing(); loadNotificationSettings(); loadPauseSettings(); }}>
            Refresh
          </Button>
        }
      />

      {/* Tabs */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider', minHeight: 48 }}
        >
          <Tab label="UPI & Invoicing" value="upi" sx={{ minHeight: 48 }} />
          <Tab label="Membership Plans" value="pricing" sx={{ minHeight: 48 }} />
          <Tab label="Event Notifications" value="notifications" sx={{ minHeight: 48 }} />
          <Tab label="Pause Membership" value="pause" sx={{ minHeight: 48 }} />
        </Tabs>
      </Paper>



      {selectedTab === 'upi' && (
        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {alerts.upi && <Alert severity={alerts.upi.type} onClose={() => setAlerts(p => ({ ...p, upi: null }))} sx={{ mb: 4, borderRadius: 2 }}>{alerts.upi.msg}</Alert>}

            {upiLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
              <Stack spacing={5}>
                {/* UPI Details Section */}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <QrCode color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={700}>Payment Configuration</Typography>
                  </Stack>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Primary UPI ID"
                        value={upiEditForm.upiId}
                        onChange={(e) => handleUpiFieldChange('upiId', e.target.value)}
                        fullWidth
                        placeholder="e.g., gym@upi"
                        helperText="Used for automated payment links"
                        InputProps={{ startAdornment: <InputAdornment position="start">@</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        border: '2px dashed', borderColor: upiEditForm.qrImage ? 'success.main' : 'divider', 
                        borderRadius: 2, p: 3, textAlign: 'center', bgcolor: upiEditForm.qrImage ? alpha(theme.palette.success.main, 0.05) : 'background.paper',
                        transition: 'all 0.2s'
                      }}>
                        {upiEditForm.qrImage ? (
                          <Stack spacing={1.5} alignItems="center">
                            <CheckCircle color="success" sx={{ fontSize: 32 }} />
                            <Typography variant="body2" fontWeight={600} color="success.main">QR Code Active</Typography>
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="outlined" color="inherit" onClick={() => setQrPreviewOpen(true)}>Preview</Button>
                              <Button component="label" size="small" variant="text" disabled={qrUploading}>
                                {qrUploading ? 'Replacing...' : 'Replace'}
                                <input type="file" hidden accept="image/*" onChange={handleQrUpload} />
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Stack spacing={1.5} alignItems="center">
                            <UploadFile sx={{ fontSize: 32, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary">Upload your UPI QR code to display on invoices</Typography>
                            <Button component="label" variant="contained" size="small" color="inherit" disabled={qrUploading} sx={{ mt: 1 }}>
                              {qrUploading ? 'Uploading...' : 'Browse Files'}
                              <input type="file" hidden accept="image/*" onChange={handleQrUpload} />
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Invoicing Section */}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <ReceiptLong color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={700}>Invoice Preferences</Typography>
                  </Stack>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Reminder Days"
                        type="number"
                        value={upiEditForm.invoiceReminderDays}
                        onChange={(e) => handleUpiFieldChange('invoiceReminderDays', parseInt(e.target.value) || 0)}
                        fullWidth
                        inputProps={{ min: 0, max: 365 }}
                        helperText="Days before due date to send automated reminders"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Custom Invoice Footer Note"
                        value={upiEditForm.invoiceNote}
                        onChange={(e) => handleUpiFieldChange('invoiceNote', e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="E.g., Thank you for training with us! Fees are non-refundable."
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 2 }}>
                  <Button variant="text" color="inherit" onClick={resetUpiForm} disabled={JSON.stringify(upiEditForm) === JSON.stringify(upiSettings)}>
                    Discard Changes
                  </Button>
                  <Button variant="contained" startIcon={<Save />} onClick={saveUpiSettings} disabled={upiSaving || JSON.stringify(upiEditForm) === JSON.stringify(upiSettings)} sx={{ px: 4, borderRadius: 2 }}>
                    {upiSaving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 'pricing' && (
        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {alerts.pricing && <Alert severity={alerts.pricing.type} onClose={() => setAlerts(p => ({ ...p, pricing: null }))} sx={{ mb: 4, borderRadius: 2 }}>{alerts.pricing.msg}</Alert>}

            {pricingLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
              <Stack spacing={5}>
                {/* Plan Base Prices */}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <LocalOffer color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={700}>Base Membership Tiers</Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {['monthly', 'quarterly', 'annual'].map((plan) => (
                      <Grid item xs={12} sm={4} key={plan}>
                        <TextField
                          label={`${plan.charAt(0).toUpperCase() + plan.slice(1)} Price`}
                          type="number"
                          value={pricingEditForm[plan]?.price || 0}
                          onChange={(e) => handlePricingFieldChange(plan, 'price', parseInt(e.target.value) || 0)}
                          fullWidth
                          inputProps={{ min: 0, step: 100 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                {/* Discounts */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Long-Term Discounts</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Offer percentage discounts to encourage members to choose quarterly or annual plans.
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Quarterly Discount"
                        type="number"
                        value={pricingEditForm.quarterly?.discount || 0}
                        onChange={(e) => handlePricingFieldChange('quarterly', 'discount', parseInt(e.target.value) || 0)}
                        fullWidth
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Annual Discount"
                        type="number"
                        value={pricingEditForm.annual?.discount || 0}
                        onChange={(e) => handlePricingFieldChange('annual', 'discount', parseInt(e.target.value) || 0)}
                        fullWidth
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Live Preview / Plan Summary */}
                <Box sx={{ bgcolor: 'action.hover', p: 3, borderRadius: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Plan Preview</Typography>
                  <Grid container spacing={2}>
                    {[
                      { name: 'Monthly', data: pricingEditForm.monthly },
                      { name: 'Quarterly', data: pricingEditForm.quarterly },
                      { name: 'Annual', data: pricingEditForm.annual }
                    ].map((plan, idx) => {
                      const basePrice = plan.data?.price || 0;
                      const discount = plan.data?.discount || 0;
                      const finalPrice = discount > 0 ? basePrice - (basePrice * (discount / 100)) : basePrice;

                      return (
                        <Grid item xs={12} sm={4} key={idx}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: '100%', borderColor: discount > 0 ? 'primary.light' : 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                              <Typography variant="body2" fontWeight={700} color="text.secondary">{plan.name}</Typography>
                              {discount > 0 && (
                                <Box sx={{ px: 1, py: 0.25, bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', borderRadius: 1, fontSize: '0.7rem', fontWeight: 800 }}>
                                  SAVE {discount}%
                                </Box>
                              )}
                            </Stack>
                            <Typography variant="h6" fontWeight={800} color="text.primary">
                              {fmtPrice(finalPrice)}
                            </Typography>
                            {discount > 0 && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled', mt: 0.5, display: 'block' }}>
                                Usually {fmtPrice(basePrice)}
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 2 }}>
                  <Button variant="text" color="inherit" onClick={resetPricingForm} disabled={JSON.stringify(pricingEditForm) === JSON.stringify(pricing)}>
                    Discard Changes
                  </Button>
                  <Button variant="contained" startIcon={<Save />} onClick={savePricing} disabled={pricingSaving || JSON.stringify(pricingEditForm) === JSON.stringify(pricing)} sx={{ px: 4, borderRadius: 2 }}>
                    {pricingSaving ? 'Saving...' : 'Save Pricing Plans'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 'notifications' && (
        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {alerts.notifications && <Alert severity={alerts.notifications.type} onClose={() => setAlerts(p => ({ ...p, notifications: null }))} sx={{ mb: 4, borderRadius: 2 }}>{alerts.notifications.msg}</Alert>}

            {notifLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
              <Stack spacing={4}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <NotifIcon color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={700}>Event Notifications Configuration</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Configure which important gym events should notify admin team members. When enabled, admins will receive push notifications for these events.
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Member Events</Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.memberEnrolled} onChange={(e) => handleNotificationFieldChange('memberEnrolled', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Member Enrolled</Typography><Typography variant="caption" color="text.secondary">Notify when a new member joins the gym</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.memberLeft} onChange={(e) => handleNotificationFieldChange('memberLeft', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Member Left</Typography><Typography variant="caption" color="text.secondary">Notify when a member's membership expires or ends</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.memberRejoined} onChange={(e) => handleNotificationFieldChange('memberRejoined', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Member Rejoined</Typography><Typography variant="caption" color="text.secondary">Notify when a former member rejoins</Typography></Box>}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Events</Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.feePaid} onChange={(e) => handleNotificationFieldChange('feePaid', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Fee Paid</Typography><Typography variant="caption" color="text.secondary">Notify when a member successfully pays fees</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.feeFailed} onChange={(e) => handleNotificationFieldChange('feeFailed', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Payment Failed</Typography><Typography variant="caption" color="text.secondary">Notify when a payment transaction fails</Typography></Box>}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Other Events</Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.staffAdded} onChange={(e) => handleNotificationFieldChange('staffAdded', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Staff Added</Typography><Typography variant="caption" color="text.secondary">Notify when new staff or trainers are added</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.planDownloaded} onChange={(e) => handleNotificationFieldChange('planDownloaded', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Plan Downloaded</Typography><Typography variant="caption" color="text.secondary">Notify when members download workout/meal plans</Typography></Box>}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Challenges & Leads</Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.challengeEnrolled} onChange={(e) => handleNotificationFieldChange('challengeEnrolled', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Challenge Enrolled</Typography><Typography variant="caption" color="text.secondary">Notify when member enrolls in challenges/events</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.newLead} onChange={(e) => handleNotificationFieldChange('newLead', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>New Lead</Typography><Typography variant="caption" color="text.secondary">Notify when a new lead is created</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.leadConverted} onChange={(e) => handleNotificationFieldChange('leadConverted', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Lead Converted</Typography><Typography variant="caption" color="text.secondary">Notify when a lead becomes a member</Typography></Box>}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activity & Engagement</Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.feedbackReceived} onChange={(e) => handleNotificationFieldChange('feedbackReceived', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Feedback Received</Typography><Typography variant="caption" color="text.secondary">Notify when members submit feedback</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.attendanceMarked} onChange={(e) => handleNotificationFieldChange('attendanceMarked', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Attendance Marked</Typography><Typography variant="caption" color="text.secondary">Notify when member attendance is recorded</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.slotBooked} onChange={(e) => handleNotificationFieldChange('slotBooked', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Slot Booked</Typography><Typography variant="caption" color="text.secondary">Notify when member books a class slot</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.memberMilestone} onChange={(e) => handleNotificationFieldChange('memberMilestone', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Member Milestone</Typography><Typography variant="caption" color="text.secondary">Notify when member reaches milestones (e.g., 100 workouts)</Typography></Box>}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alerts & Batch Management</Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.lowAttendanceAlert} onChange={(e) => handleNotificationFieldChange('lowAttendanceAlert', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Low Attendance Alert</Typography><Typography variant="caption" color="text.secondary">Alert when member has low attendance</Typography></Box>}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={notificationEditForm.batchCreated} onChange={(e) => handleNotificationFieldChange('batchCreated', e.target.checked)} />}
                        label={<Box><Typography variant="body2" fontWeight={600}>Batch Created</Typography><Typography variant="caption" color="text.secondary">Notify when new batch is created</Typography></Box>}
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 2 }}>
                  <Button variant="text" color="inherit" onClick={resetNotificationForm} disabled={JSON.stringify(notificationEditForm) === JSON.stringify(notificationSettings)}>
                    Discard Changes
                  </Button>
                  <Button variant="contained" startIcon={<Save />} onClick={saveNotificationSettings} disabled={notifSaving || JSON.stringify(notificationEditForm) === JSON.stringify(notificationSettings)} sx={{ px: 4, borderRadius: 2 }}>
                    {notifSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 'pause' && (
        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', boxShadow: '0 4px 24px rgba(0,0,0,0.02)' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {alerts.pause && (
              <Alert severity={alerts.pause.type} onClose={() => setAlerts(p => ({ ...p, pause: null }))} sx={{ mb: 4, borderRadius: 2 }}>
                {alerts.pause.msg}
              </Alert>
            )}

            {pauseLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
              <Stack spacing={4}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <PauseCircle color="primary" sx={{ fontSize: 28 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>Membership Pause Configuration</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Control whether gym members can pause their memberships directly in the Fitistic app.
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pauseEditForm.allowPauseMembership}
                        onChange={(e) => handlePauseFieldChange('allowPauseMembership', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Enable Membership Pause
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When enabled, gym members will see a "Pause Membership" option in their Fitistic app profile section.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Pause Days Allowed Per Member"
                      type="number"
                      value={pauseEditForm.maxPauseDays}
                      onChange={(e) => handlePauseFieldChange('maxPauseDays', Math.max(0, Math.min(365, parseInt(e.target.value, 10) || 0)))}
                      fullWidth
                      disabled={!pauseEditForm.allowPauseMembership}
                      helperText="Maximum number of pause days allowed per membership period (0 - 365 days)."
                      InputProps={{ inputProps: { min: 0, max: 365 } }}
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 2 }}>
                  <Button variant="text" color="inherit" onClick={resetPauseForm} disabled={JSON.stringify(pauseEditForm) === JSON.stringify(pauseSettings)}>
                    Discard Changes
                  </Button>
                  <Button variant="contained" startIcon={<Save />} onClick={savePauseSettings} disabled={pauseSaving || JSON.stringify(pauseEditForm) === JSON.stringify(pauseSettings)} sx={{ px: 4, borderRadius: 2 }}>
                    {pauseSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={qrPreviewOpen} onClose={() => setQrPreviewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight={700}>QR Code Preview</Typography>
          <IconButton onClick={() => setQrPreviewOpen(false)} size="small" sx={{ color: 'text.secondary' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3, textAlign: 'center' }}>
          {upiEditForm.qrImage && (
            <Box component="img" src={getQrImageUrl(upiEditForm.qrImage)} sx={{ width: '100%', maxWidth: 300, height: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} alt="QR Code" />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

